import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** English mirror of the Thai corporate-documents-for-abroad guide. */
const TITLE = "Thai Company Documents for Use Abroad — Getting Them Accepted";
const DESC =
  "Which Thai corporate documents need which layer of certification, the order of the steps, and the checks that keep a company file from being returned.";
const PATH = "/blog/company-documents-for-abroad";
const URL = `/en${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Which Thai company documents are usually requested abroad?",
    answer:
      "Most commonly the company affidavit or certificate of incorporation, the shareholder list, the memorandum of association, financial statements, board or shareholder resolutions, and a power of attorney authorising the person who will act abroad.",
  },
  {
    question: "What is the difference between an official record and a private document?",
    answer:
      "Records issued by the Department of Business Development, such as the company affidavit and shareholder list, are official records and can normally go straight to certification. Documents produced by the company itself, such as resolutions or letters, are private documents and usually need a notarial services attorney to certify signatures first.",
  },
  {
    question: "In what order should the steps be completed?",
    answer:
      "Obtain a current issue of the official records, notarise the private documents, certify the set with the Department of Consular Affairs where required, translate into the language the destination accepts, certify the translation, and finally complete the embassy legalisation or apostille step.",
  },
  {
    question: "How current must a company affidavit be?",
    answer:
      "Receiving authorities and banks commonly require an issue no older than one to three months. Because certification adds working days, request the affidavit only once the rest of the file is close to ready.",
  },
  {
    question: "Do financial statements need special treatment?",
    answer:
      "Financial statements are usually submitted as filed, and destinations may ask for auditor identification or a certified copy of the filing receipt. Confirm whether the destination wants the filed set or an audited set before certification, because the two are not interchangeable.",
  },
  {
    question: "Who should sign a board resolution intended for use abroad?",
    answer:
      "The directors authorised to bind the company under the affidavit, in the numbers and combination the affidavit states, with the company seal where the affidavit requires it. A resolution signed by a person not shown as an authorised director is the fastest route to rejection.",
  },
  {
    question: "What causes company files to be returned?",
    answer:
      "Out-of-date affidavits, signatures from directors without binding authority, incomplete translations that omit registrar endorsements, English company names that differ between documents, and certification performed before the private documents were notarised.",
  },
];

export const Route = createFileRoute("/en/blog/company-documents-for-abroad")({
  component: EnCompanyDocumentsGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "en" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "en") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "thai company affidavit translation, dbd company certificate legalisation, board resolution notarisation thailand, corporate documents apostille thailand, shareholder list certified translation",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "en"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: "/en" },
              { name: "Blog", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; note: string }> = [
  {
    doc: "Company affidavit / certificate of incorporation",
    note: "Official record; request a current issue and check the binding-authority clause before signing anything.",
  },
  {
    doc: "Shareholder list",
    note: "Official record; confirm the shareholding matches what the destination has been told.",
  },
  {
    doc: "Memorandum of association",
    note: "Official record; usually requested where the destination reviews the objects of the company.",
  },
  {
    doc: "Financial statements",
    note: "Confirm whether the filed set or an audited set is required, then certify only that set.",
  },
  {
    doc: "Board or shareholder resolutions",
    note: "Private document; signatures certified by a notarial services attorney before certification.",
  },
  {
    doc: "Power of attorney",
    note: "Private document; draft the scope to cover every office and follow-on act, then notarise.",
  },
];

const SEQUENCE: string[] = [
  "Confirm the destination requirement, accepted language, and how recent the records must be",
  "Obtain current issues of the official records from the Department of Business Development",
  "Prepare private documents and have the signatures notarised",
  "Certify the set with the Department of Consular Affairs where required",
  "Translate the full set, including registrar endorsements and seal text",
  "Certify the translation and complete the embassy legalisation or apostille step",
];

const MISTAKES: string[] = [
  "An affidavit issued too early to satisfy the destination's recency rule",
  "Resolutions signed by directors without binding authority under the affidavit",
  "English company names spelled differently across documents in one file",
  "Translations that omit registrar endorsements, seals, or page stamps",
  "Certifying private documents before the signatures were notarised",
  "Submitting an audited set where the filed set was requested, or the reverse",
];

function EnCompanyDocumentsGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/en" className="text-muted-foreground hover:text-foreground">
          Home
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Blog
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="en" />
      <p className="answer-summary speakable-summary">
        A Thai company file is accepted abroad when two things are true: each document carries the
        right layer of certification for its type, and the whole set tells one consistent story about
        the company. Official registry records go to certification directly; documents the company
        wrote itself are notarised first.
      </p>

      <h2>1. Document-by-document checklist</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}:</strong> {d.note}
          </li>
        ))}
      </ul>

      <h2>2. The workflow</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Binding authority is the checkpoint</h2>
      <p>
        Before any resolution or contract is signed, read the binding-authority clause of the current
        affidavit and match the signatories and the seal requirement exactly. This single check
        prevents the most expensive category of rejection, because a resolution signed incorrectly must
        be re-passed and then travel through the entire certification chain again.
      </p>

      <h2>4. Consistency across the set</h2>
      <p>
        Receiving officers compare the affidavit, the shareholder list, the resolutions, and the
        translations against each other. The English company name, the registered address, the
        director names, and the share figures must agree everywhere. Where a change has occurred, put
        the registry record that evidences it in the file rather than explaining it in a letter.
      </p>

      <h2>5. Reasons company files are returned</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>6. Frequently asked questions</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. References and related services</h2>
      <p className="text-sm">
        References:{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          Department of Business Development, Ministry of Commerce (Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/en/corporate" className="text-primary underline">
            Corporate document service
          </Link>
        </li>
        <li>
          <Link to="/en/notary" className="text-primary underline">
            Notarial services attorney
          </Link>
        </li>
        <li>
          <Link to="/en/blog/power-of-attorney-abroad-guide" className="text-primary underline">
            Power of attorney for use abroad
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Content reviewed on {PUBLISHED}. This guide is general information for planning purposes and
        is not legal advice on a specific matter. Please confirm current requirements with the
        receiving authority before you proceed.
      </p>
    </article>
  );
}
